import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import type {
  AdjustPointsRequest,
  CreateCustomerRequest,
  CustomerDto,
  UpdateCustomerRequest,
} from './types';

const QK = (search: string) => ['customers', search] as const;

export function useCustomers(search: string) {
  return useQuery({
    queryKey: QK(search),
    queryFn: async () =>
      (await apiClient.get<CustomerDto[]>('/api/customers', {
        params: search ? { search } : undefined,
      })).data,
  });
}

export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (request: CreateCustomerRequest) =>
      (await apiClient.post<CustomerDto>('/api/customers', request)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers'] }),
  });
}

export function useUpdateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, request }: { id: string; request: UpdateCustomerRequest }) =>
      (await apiClient.put<CustomerDto>(`/api/customers/${id}`, request)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers'] }),
  });
}

export function useDeleteCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/customers/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers'] }),
  });
}

export function useAdjustPoints() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, request }: { id: string; request: AdjustPointsRequest }) =>
      (await apiClient.post<CustomerDto>(`/api/customers/${id}/points`, request)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers'] }),
  });
}
